'use client';

import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCcw, Home } from 'lucide-react';
import Link from 'next/link';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service if needed
    console.error('Next.js Page Error:', error);
  }, [error]);

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-6 text-center">
      <div className="h-20 w-20 rounded-full bg-destructive/10 flex items-center justify-center mb-8">
        <AlertCircle className="h-10 w-10 text-destructive" />
      </div>
      
      <h1 className="text-3xl md:text-4xl font-black font-headline tracking-tight mb-4">
        Report Execution Failed
      </h1>
      
      <p className="text-muted-foreground text-lg max-w-md mb-10 leading-relaxed">
        We encountered an issue while loading this section of the newsroom. 
        This might be due to a brief connection sync or maintenance.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xs justify-center">
        <Button 
          onClick={() => reset()}
          className="bg-primary hover:bg-primary/90 font-bold h-12 px-8 flex items-center gap-2"
        >
          <RefreshCcw className="h-4 w-4" />
          Try Again
        </Button>
        <Button variant="outline" asChild className="h-12 px-8 font-bold border-primary/20">
          <Link href="/">
            <Home className="mr-2 h-4 w-4" />
            Home
          </Link>
        </Button>
      </div>
      
      {error.digest && (
        <div className="mt-12 pt-8 border-t border-border/50 w-full max-w-xs">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground opacity-50">
            Internal ID: {error.digest}
          </p>
        </div>
      )}
    </div>
  );
}
